// composables/useApiClient.ts
import { createApiClient, type ApiClient } from '@/api/generated/apiClient';
import { problemCapturingFetch } from '@/api/problemDetails';
import { acquireApiToken } from '@/auth/msal';
import { AllowedHostsValidator, BaseBearerTokenAuthenticationProvider, type AccessTokenProvider } from '@microsoft/kiota-abstractions';
import { FetchRequestAdapter, KiotaClientFactory } from '@microsoft/kiota-http-fetchlibrary';

let client: ApiClient | null = null;

const accessTokenProvider: AccessTokenProvider = {
    getAuthorizationToken: () => acquireApiToken(),
    getAllowedHostsValidator: () => new AllowedHostsValidator()
};

function createClient(): ApiClient {
    const authProvider = new BaseBearerTokenAuthenticationProvider(accessTokenProvider);
    // The default middleware chain, ending in a fetch that keeps the ProblemDetails body of a
    // failed response so DrawerHost can show it; see api/problemDetails.ts.
    const httpClient = KiotaClientFactory.create(problemCapturingFetch);
    const adapter = new FetchRequestAdapter(authProvider, undefined, undefined, httpClient);
    // Set explicitly: the generated client's own default is whatever host the OpenAPI document was
    // fetched from at generation time, which has been staging and a temporary port before.
    adapter.baseUrl = apiBaseUrl();
    return createApiClient(adapter);
}

// The InternalApi's base URL, for the generated client and the notification hub under it.
// VITE_API_BASE_URL wins when set, for pointing a build at some other API host.
// Otherwise a production build targets this app's own origin under /api: deployed, the app is
// served by a YARP container that reverse-proxies that path to the InternalApi, so the call
// never crosses an origin and no CORS is involved. Kiota builds URI templates from the base
// URL and needs it absolute, hence window.location.origin rather than a bare "/api".
// In dev there is no proxy and the InternalApi launch profile listens on https://localhost:5001.
// In mock mode (`npm run dev:mock`) the API is the MSW worker inside this page, which only
// answers requests to the page's own origin, so the origin is used whatever port the dev server
// got. Vite inlines MODE, so a production build compares two constants and drops the check.
export function apiBaseUrl(): string {
    const sameOrigin = import.meta.env.PROD || import.meta.env.MODE === 'mock';
    return import.meta.env.VITE_API_BASE_URL || (sameOrigin ? `${window.location.origin}/api` : 'https://localhost:5001');
}

export function useApiClient() {
    if (!client) {
        client = createClient();
    }
    return client;
}
